import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { 
  Shield, 
  Smartphone, 
  Key, 
  AlertTriangle, 
  Check, 
  X, 
  Copy, 
  Eye, 
  EyeOff,
  Monitor,
  Clock,
  MapPin,
  Trash2,
  RefreshCw,
  Lock,
  Unlock,
  QrCode
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = window.location.origin;

export default function SecurityAdvancedPage() {
  const [loading, setLoading] = useState(false);
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [showSetup2FA, setShowSetup2FA] = useState(false);
  const [qrCode, setQrCode] = useState('');
  const [manualKey, setManualKey] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [password, setPassword] = useState('');
  const [loginHistory, setLoginHistory] = useState([]);
  const [trustedDevices, setTrustedDevices] = useState([]);
  const [securitySettings, setSecuritySettings] = useState({
    login_notifications: true,
    session_timeout_minutes: 30,
    require_2fa_for_sensitive: false
  });

  const token = localStorage.getItem('token');
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };

  useEffect(() => {
    fetchSecurityStatus();
    fetchLoginHistory();
    fetchTrustedDevices();
  }, []);

  const fetchSecurityStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/api/security/2fa/status`, { headers });
      if (response.ok) {
        const data = await response.json();
        setTwoFactorEnabled(data.enabled);
      }

      const settingsResponse = await fetch(`${API_URL}/api/security/settings`, { headers });
      if (settingsResponse.ok) {
        const settings = await settingsResponse.json();
        setSecuritySettings(settings);
      }
    } catch (error) {
      console.error('Error fetching security status:', error);
    }
  };

  const fetchLoginHistory = async () => {
    try {
      const response = await fetch(`${API_URL}/api/security/login-history?limit=10`, { headers });
      if (response.ok) {
        const data = await response.json();
        setLoginHistory(data.login_history || []);
      }
    } catch (error) {
      console.error('Error fetching login history:', error);
    }
  };

  const fetchTrustedDevices = async () => {
    try {
      const response = await fetch(`${API_URL}/api/security/trusted-devices`, { headers });
      if (response.ok) {
        const data = await response.json();
        setTrustedDevices(data.devices || []);
      }
    } catch (error) {
      console.error('Error fetching trusted devices:', error);
    }
  };

  const startSetup2FA = async () => {
    if (!password) {
      toast.error('أدخل كلمة المرور');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/security/2fa/setup`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ password })
      });

      const data = await response.json();
      if (response.ok) {
        setQrCode(data.qr_code);
        setManualKey(data.manual_key);
        setShowSetup2FA(true);
        toast.success('امسح QR Code باستخدام Google Authenticator');
      } else {
        toast.error(data.detail || 'حدث خطأ');
      }
    } catch (error) {
      toast.error('حدث خطأ في الاتصال');
    }
    setLoading(false);
  };

  const verifyAndEnable2FA = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error('أدخل رمز التحقق المكون من 6 أرقام');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/security/2fa/verify-setup`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ code: verificationCode })
      });

      const data = await response.json();
      if (response.ok) {
        setTwoFactorEnabled(true);
        setBackupCodes(data.backup_codes);
        setShowBackupCodes(true);
        setShowSetup2FA(false);
        setVerificationCode('');
        setPassword('');
        toast.success('تم تفعيل المصادقة الثنائية بنجاح! 🔐');
      } else {
        toast.error(data.detail || 'الرمز غير صحيح');
      }
    } catch (error) {
      toast.error('حدث خطأ في الاتصال');
    }
    setLoading(false);
  };

  const disable2FA = async () => {
    if (!password || !verificationCode) {
      toast.error('أدخل كلمة المرور ورمز المصادقة');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/security/2fa/disable`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ password, code: verificationCode })
      });

      const data = await response.json();
      if (response.ok) {
        setTwoFactorEnabled(false);
        setPassword('');
        setVerificationCode('');
        toast.success('تم تعطيل المصادقة الثنائية');
      } else {
        toast.error(data.detail || 'حدث خطأ');
      }
    } catch (error) {
      toast.error('حدث خطأ في الاتصال');
    }
    setLoading(false);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('تم النسخ!');
  };

  const removeTrustedDevice = async (fingerprint) => {
    try {
      const response = await fetch(`${API_URL}/api/security/trusted-devices/${fingerprint}`, {
        method: 'DELETE',
        headers
      });

      if (response.ok) {
        toast.success('تم إزالة الجهاز');
        fetchTrustedDevices();
      }
    } catch (error) {
      toast.error('حدث خطأ');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('ar-IQ', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="security-advanced-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
          <Shield className="w-6 h-6 text-emerald-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">الأمان المتقدم</h1>
          <p className="text-gray-500 text-sm">حماية حسابك بميزات أمان إضافية</p>
        </div>
      </div>

      {/* المصادقة الثنائية */}
      <Card className="border-2 border-emerald-200 dark:border-emerald-800">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Smartphone className="w-5 h-5 text-emerald-600" />
              <div>
                <CardTitle className="text-lg">المصادقة الثنائية (2FA)</CardTitle>
                <CardDescription>حماية إضافية باستخدام Google Authenticator</CardDescription>
              </div>
            </div>
            <Badge variant={twoFactorEnabled ? "default" : "secondary"} className={twoFactorEnabled ? "bg-emerald-500" : ""}>
              {twoFactorEnabled ? (
                <><Lock className="w-3 h-3 me-1" /> مفعّل</>
              ) : (
                <><Unlock className="w-3 h-3 me-1" /> غير مفعّل</>
              )}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {!twoFactorEnabled && !showSetup2FA && (
            <div className="space-y-4">
              <div className="bg-amber-50 dark:bg-amber-900/20 p-4 rounded-lg border border-amber-200 dark:border-amber-800">
                <div className="flex gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-800 dark:text-amber-200">ننصحك بتفعيل المصادقة الثنائية</p>
                    <p className="text-sm text-amber-700 dark:text-amber-300">تضيف طبقة حماية إضافية لحسابك</p>
                  </div>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>كلمة المرور (للتأكيد)</Label>
                <Input 
                  type="password" 
                  value={password} 
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="أدخل كلمة المرور"
                />
              </div>
              
              <Button onClick={startSetup2FA} disabled={loading} className="w-full bg-emerald-600 hover:bg-emerald-700">
                <Key className="w-4 h-4 me-2" />
                تفعيل المصادقة الثنائية
              </Button>
            </div>
          )}

          {showSetup2FA && (
            <div className="space-y-4">
              <div className="text-center">
                <p className="mb-4 font-medium">امسح QR Code باستخدام تطبيق Google Authenticator</p>
                {qrCode && (
                  <div className="flex justify-center mb-4">
                    <img src={qrCode} alt="QR Code" className="w-48 h-48 border rounded-lg" />
                  </div>
                )}
                <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
                  <span>أو أدخل المفتاح يدوياً:</span>
                  <code className="bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded text-xs">{manualKey}</code>
                  <Button variant="ghost" size="sm" onClick={() => copyToClipboard(manualKey)}>
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label>رمز التحقق من التطبيق</Label>
                <Input 
                  type="text" 
                  value={verificationCode} 
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="أدخل الرمز المكون من 6 أرقام"
                  maxLength={6}
                  className="text-center text-2xl tracking-widest"
                />
              </div>

              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowSetup2FA(false)} className="flex-1">
                  إلغاء
                </Button>
                <Button onClick={verifyAndEnable2FA} disabled={loading} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                  <Check className="w-4 h-4 me-2" />
                  تأكيد وتفعيل
                </Button>
              </div>
            </div>
          )}

          {twoFactorEnabled && !showSetup2FA && (
            <div className="space-y-4">
              <div className="bg-emerald-50 dark:bg-emerald-900/20 p-4 rounded-lg border border-emerald-200 dark:border-emerald-800">
                <div className="flex gap-2">
                  <Check className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-emerald-800 dark:text-emerald-200">المصادقة الثنائية مفعّلة</p>
                    <p className="text-sm text-emerald-700 dark:text-emerald-300">حسابك محمي بطبقة أمان إضافية</p>
                  </div>
                </div>
              </div>

              <div className="border-t pt-4 space-y-4">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">لتعطيل المصادقة الثنائية:</p>
                <div className="grid gap-2">
                  <Input 
                    type="password" 
                    value={password} 
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="كلمة المرور"
                  />
                  <Input 
                    type="text" 
                    value={verificationCode} 
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="رمز المصادقة الثنائية"
                    maxLength={6}
                  />
                </div>
                <Button variant="destructive" onClick={disable2FA} disabled={loading} className="w-full">
                  <X className="w-4 h-4 me-2" />
                  تعطيل المصادقة الثنائية
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* الأكواد الاحتياطية */}
      {showBackupCodes && backupCodes.length > 0 && (
        <Card className="border-2 border-amber-200 dark:border-amber-800">
          <CardHeader>
            <div className="flex items-center gap-3">
              <Key className="w-5 h-5 text-amber-600" />
              <div>
                <CardTitle className="text-lg">الأكواد الاحتياطية</CardTitle>
                <CardDescription className="text-amber-600">احفظ هذه الأكواد في مكان آمن!</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="bg-amber-50 dark:bg-amber-900/20 p-4 rounded-lg mb-4">
              <p className="text-sm text-amber-800 dark:text-amber-200">
                استخدم هذه الأكواد إذا فقدت الوصول لتطبيق المصادقة. كل كود يستخدم مرة واحدة فقط.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2 mb-4">
              {backupCodes.map((code, index) => (
                <div key={index} className="bg-gray-100 dark:bg-gray-800 p-2 rounded text-center font-mono">
                  {code}
                </div>
              ))}
            </div>
            <Button onClick={() => copyToClipboard(backupCodes.join('\n'))} className="w-full">
              <Copy className="w-4 h-4 me-2" />
              نسخ جميع الأكواد
            </Button>
          </CardContent>
        </Card>
      )}

      {/* سجل تسجيلات الدخول */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-blue-600" />
              <div>
                <CardTitle className="text-lg">سجل تسجيلات الدخول</CardTitle>
                <CardDescription>آخر عمليات تسجيل الدخول لحسابك</CardDescription>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={fetchLoginHistory}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loginHistory.length === 0 ? (
            <p className="text-center text-gray-500 py-4">لا يوجد سجل تسجيلات دخول</p>
          ) : (
            <div className="space-y-3">
              {loginHistory.map((log, index) => (
                <div key={index} className={`flex items-center justify-between p-3 rounded-lg ${
                  log.success 
                    ? 'bg-gray-50 dark:bg-gray-800' 
                    : 'bg-red-50 dark:bg-red-900/20'
                }`}>
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-full ${
                      log.success 
                        ? 'bg-emerald-100 dark:bg-emerald-900/30' 
                        : 'bg-red-100 dark:bg-red-900/30'
                    }`}>
                      {log.success ? (
                        <Check className="w-4 h-4 text-emerald-600" />
                      ) : (
                        <X className="w-4 h-4 text-red-600" />
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <Monitor className="w-4 h-4 text-gray-400" />
                        <span className="text-sm">
                          {log.device_info?.browser} - {log.device_info?.os}
                        </span>
                        {log.is_new_device && (
                          <Badge variant="outline" className="text-xs">جهاز جديد</Badge>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {formatDate(log.timestamp)}
                      </div>
                    </div>
                  </div>
                  <div className="text-left text-sm text-gray-500">
                    {log.ip_address}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* الأجهزة الموثوقة */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <Monitor className="w-5 h-5 text-purple-600" />
            <div>
              <CardTitle className="text-lg">الأجهزة الموثوقة</CardTitle>
              <CardDescription>الأجهزة التي لا تحتاج المصادقة الثنائية</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {trustedDevices.length === 0 ? (
            <p className="text-center text-gray-500 py-4">لا يوجد أجهزة موثوقة</p>
          ) : (
            <div className="space-y-3">
              {trustedDevices.map((device, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Monitor className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="font-medium">{device.name}</p>
                      <p className="text-xs text-gray-500">
                        {device.device_info?.browser} - {device.device_info?.os}
                      </p>
                      <p className="text-xs text-gray-400">
                        آخر استخدام: {formatDate(device.last_used)}
                      </p>
                    </div>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => removeTrustedDevice(device.fingerprint)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* إعدادات الأمان */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <Shield className="w-5 h-5 text-gray-600" />
            <div>
              <CardTitle className="text-lg">إعدادات الأمان</CardTitle>
              <CardDescription>تخصيص إعدادات الأمان لحسابك</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">إشعارات تسجيل الدخول</p>
              <p className="text-sm text-gray-500">تلقي إشعار عند تسجيل الدخول من جهاز جديد</p>
            </div>
            <Switch 
              checked={securitySettings.login_notifications}
              onCheckedChange={(checked) => setSecuritySettings(prev => ({...prev, login_notifications: checked}))}
            />
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">مهلة الجلسة</p>
              <p className="text-sm text-gray-500">تسجيل الخروج تلقائياً بعد فترة عدم نشاط</p>
            </div>
            <select 
              value={securitySettings.session_timeout_minutes}
              onChange={(e) => setSecuritySettings(prev => ({...prev, session_timeout_minutes: parseInt(e.target.value)}))}
              className="border rounded-lg px-3 py-2 bg-white dark:bg-gray-800"
            >
              <option value={15}>15 دقيقة</option>
              <option value={30}>30 دقيقة</option>
              <option value={60}>ساعة</option>
              <option value={120}>ساعتين</option>
            </select>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
